<template>
    <div>

        <PageHeader :title="$t('generateGdzieKupicTo')" :subtitle="$t('generateGdzieKupicToDescription')">
            <template #bottom>
                <div class="d-flex gap-3 flex-wrap">
                    <ButtonIcon
                        variant="link"
                        class="p-0"
                        icon="file-text"
                        :icon-size="24"
                        @click="openHelp"
                    >
                        {{ $t('takeALookAtHelp') }}
                    </ButtonIcon>
                    <ButtonIcon
                        href="https://getbuybox.com/jak-utworzyc-gdziekupic-to-widzet-w-formie-linku-na-social-media/"
                        target="_blank"
                        variant="link"
                        class="p-0"
                        icon="youtube"
                        :icon-size="24"
                    >
                        {{ $t('watchVideo') }}
                    </ButtonIcon>
                </div>
            </template>
        </PageHeader>

        <div class="d-flex gap-4">

            <div class="d-flex flex-column flex-grow-1 flex-basis-0">

                <Card :header="`1. ${$t('selectProduct')}`" :state="step1State">
                    <ProductSearch id="form-product" v-model="product" class="product-search" />
                </Card>

                <Card :header="`2. ${$t('choosePage')}`" :state="step2State">
                    <div v-show="productId" class="d-flex flex-column gap-3">
                        <Multiselect
                            id="form-space"
                            v-model="space"
                            :initial-value="Number.parseInt($route.query.space) || undefined"
                            :fetch-options-function="fetchSpaces"
                            searchable
                            track-by="id"
                            label="name"
                            select-label=""
                            :placeholder="$t('selectWebsite')"
                        />
                    </div>
                </Card>

                <Card :header="`3. ${$t('copyUrlAndPutItOnThePage')}`" :state="step3State">
                    <pre class="widget-code">{{ widgetUrl }}</pre>
                    <div class="my-3">
                        <ButtonIcon
                            variant="link"
                            class="expandable p-0"
                            :class="{ 'collapsed': !showParameters }"
                            icon="chevron-up"
                            flip-icon
                            @click="showParameters = !showParameters"
                        >
                            {{ $t('addYourOwnParameters') }}
                        </ButtonIcon>
                        <b-collapse v-model="showParameters">
                            <div class="parameters">
                                <div class="my-2"></div>
                                <b-form-group id="form-group-abpar1" :label="$t('abpar1')" label-cols-sm="3">
                                    <b-form-input id="form-abpar1" v-model="options.abpar1" />
                                </b-form-group>
                                <b-form-group id="form-group-abpar2" :label="$t('abpar2')" label-cols-sm="3">
                                    <b-form-input id="form-abpar2" v-model="options.abpar2" />
                                </b-form-group>
                                <b-form-group id="form-group-abpar3" :label="$t('abpar3')" label-cols-sm="3">
                                    <b-form-input id="form-abpar3" v-model="options.abpar3" />
                                </b-form-group>
                            </div>
                        </b-collapse>
                    </div>
                    <ButtonIcon variant="success" icon="copy" @click="copyWidgetUrl()">{{ $t('copyUrl') }}</ButtonIcon>
                </Card>

            </div>

            <div class="d-none d-md-flex flex-column flex-grow-1 flex-basis-0 card">
                <div class="d-flex justify-content-center align-items-center bg-soft-secondary px-5 py-3 rounded-top">
                    <div class="d-flex justify-content-center align-items-center bg-white p-2 width-max-500 flex-grow-1 rounded text-secondary">
                        <span>https://gdziekupic.to</span>
                    </div>
                </div>

                <div class="d-flex justify-content-center align-items-center flex-grow-1 text-center">

                    <LandingPagePreview
                        v-if="spaceId"
                        :sid="spaceId"
                        :oid="offerId"
                        :lp="1"
                        :class="{'w-100 h-100 text-center': !widgetLoading}"
                        @BBLoading="widgetLoading = true"
                        @NoData="widgetLoading = false"
                        @BBLoaded="widgetLoading = false"
                    />

                    <span v-else class="font-size-16 pre-line m-4" v-html="$t('chooseAProductAndAPageToSeeThePreview')"></span>

                    <b-spinner v-if="widgetLoading" />

                </div>

            </div>

        </div>
    </div>
</template>



<script>
    import { generateId } from '@/utils/util';
    import spaceService from '@/services/spaceService';
    import ProductSearch from '@/components/common/ProductSearch.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import LandingPagePreview from '@/components/common/LandingPagePreview.vue';

    const DEFAULT_OPTIONS = Object.freeze({
        abpar1: null,
        abpar2: null,
        abpar3: null,
    });

    export default {
        name: 'ProductLandingPage',
        components: {
            ProductSearch,
            Multiselect,
            LandingPagePreview,
        },
        data() {
            return {
                widgetLandingPageUrl: process.env.VUE_APP_WIDGET_PREVIEW_URL,
                options: { ...DEFAULT_OPTIONS },
                product: null,
                space: null,
                showParameters: false,
                widgetLoading: false,
            };
        },
        computed: {
            productId() {
                return this.product?.id || null;
            },
            offerId() {
                return this.product?.offerId || null;
            },
            spaceId() {
                return this.space?.id || null;
            },
            step1State() {
                return this.productId ? 'completed' : 'default';
            },
            step2State() {
                if (!this.productId) return 'disabled';
                if (this.spaceId) return 'completed';
                return 'default';
            },
            step3State() {
                if (!this.spaceId) return 'disabled';
                return 'default';
            },
            widgetUrl() {
                const attrs = {};
                if (this.spaceId) attrs.sid = this.spaceId;
                if (this.offerId) attrs.oid = this.offerId;
                if (this.options.abpar1) attrs.p1 = this.options.abpar1;
                if (this.options.abpar2) attrs.p2 = this.options.abpar2;
                if (this.options.abpar3) attrs.p3 = this.options.abpar3;
                const attrsStr = Object.keys(attrs)
                    .map(name => `${name}=${attrs[name]}`)
                    .join('&');
                return `${this.widgetLandingPageUrl}?${attrsStr}`;
            },
        },
        watch: {
            product() {
                this.widgetId = generateId();
                this.space = null;
            },
        },
        methods: {
            openHelp() {
                window.Gleap.openHelpCenterArticle('22', false);
            },
            fetchSpaces() {
                const userId = this.$store.getters['auth/getUser'].id;
                return spaceService.fetchSpaces(userId, { statuses: '1,2' }, false).then(data => data?.items || []);
            },
            async copyWidgetUrl() {
                if (this.widgetUrl) {
                    await this.$copyText(this.widgetUrl);
                    this.$toastr.success(this.$t('theUrlSAddressHasBeenCopied'));
                }
            },
        },
    };
</script>



<style lang="scss" scoped>
    .product-search {
        display: flex;
        flex-direction: column-reverse;

        ::v-deep {
            .product-box {
                display: flex;
                align-items: flex-start;
            }
        }

        &.touched {
            ::v-deep {
                .selected-product {
                    display: none;
                }
            }
        }
    }

    .widget-code {
        border: 1px solid $pre-color;
        border-radius: $border-radius;
        white-space: wrap;
        font-size: 90%;
        color: #6c757d;
        line-height: 150%;
        padding: 16px;
        white-space: preserve;
    }

    ::v-deep {
        .card {
            transition: all 0.15s ease;

            &.collapsed {
                background: transparent;
                box-shadow: none;

                .card-body {
                    padding-top: 0;
                    padding-bottom: 0;

                    .header-title {
                        .options-header-text {
                            font-size: $font-size-base;
                            font-weight: normal;
                            color: $body-color;
                            opacity: 1;
                        }
                    }
                }
            }
        }
    }

    .parameters {
        display: flex;
        flex-direction: column;
    }

    .btn-preview-icon {
        &:active,
        &:focus {
            box-shadow: none !important;
        }
    }
</style>
